import { getAuthHeaders } from './authService';
import { QRStyleConfig } from '../utils/qrStyleUtils';
import { getUploadUrl, uploadToS3 } from './imageService';

const API_BASE_URL = import.meta.env.VITE_API_ENDPOINT || '';

export const updateQRStyle = async (qrStyle: QRStyleConfig): Promise<void> => {
  const headers = await getAuthHeaders();
  await fetch(`${API_BASE_URL}api/users/me`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify({ qrStyle }),
  });
  // Errors silently fail — localStorage remains as fallback
};

/** How many team members (sub-users) the account has; for the Dashboard's set-up list (#410). */
export const countTeamMembers = async (): Promise<number> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}api/sub-users`, {
    headers: { 'Content-Type': 'application/json', ...headers },
  });
  if (!response.ok) throw new Error(`Failed to fetch team members: ${response.status}`);
  const data = await response.json();
  return Array.isArray(data.subUsers) ? data.subUsers.length : 0;
};

export const updateTheme = async (theme: 'light' | 'dark'): Promise<void> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}api/users/me`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify({ theme }),
  });
  if (!response.ok) {
    throw new Error(`Failed to update theme: ${response.status}`);
  }
};

/**
 * Save the account's public brand name (#395). Throws with the API's own
 * message (e.g. the validation rule that failed) so the form can show it.
 */
export const updateBrandName = async (displayName: string): Promise<{ displayName?: string }> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}api/users/me`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify({ displayName }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = body?.details?.[0]?.message || body?.message;
    throw new Error(message || "We couldn't save your brand name. Please try again.");
  }
  return body;
};

/** A change to the brand's logos and colours (#462): undefined keeps, null removes. */
export interface BrandStyleChange {
  logoSquareKey?: string | null;
  logoWideKey?: string | null;
  colors?: { primary: string; accent: string } | null;
}

/** The brand's logos (as URLs) and colours, as the API returns them. */
export interface SavedBrandStyle {
  logoSquareUrl?: string;
  logoWideUrl?: string;
  primaryColor?: string;
  accentColor?: string;
}

/**
 * Save the brand's logos and colours (#462). Throws with the API's message
 * (a logo that isn't the account's upload, a colour that isn't #RRGGBB).
 */
export const updateBrandStyle = async (brandStyle: BrandStyleChange): Promise<SavedBrandStyle | undefined> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}api/users/me`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify({ brandStyle }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = body?.details?.[0]?.message || body?.message;
    throw new Error(message || "We couldn't save your logo and colours. Please try again.");
  }
  return body?.brand;
};

/** Logo files a brand can upload: no SVG, which can carry script. */
export const BRAND_LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
export const BRAND_LOGO_MAX_BYTES = 5 * 1024 * 1024;

/** Why the file can't be a logo, or null. */
export const brandLogoProblem = (file: File): string | null => {
  if (!BRAND_LOGO_TYPES.includes(file.type)) return 'Use a PNG, JPG or WebP image.';
  if (file.size === 0) return 'This file is empty.';
  if (file.size > BRAND_LOGO_MAX_BYTES) return 'Use an image of 5 MB or less.';
  return null;
};

/**
 * Upload a logo into the account's brand folder and return its key, to save
 * with updateBrandStyle. The square logo takes slot 0, the wide one slot 1.
 */
export const uploadBrandLogo = async (file: File, slot: 'square' | 'wide'): Promise<string> => {
  const { uploadUrl, s3Key } = await getUploadUrl(file.name, file.type, file.size, slot === 'square' ? 0 : 1, 'brand');
  await uploadToS3(file, uploadUrl);
  return s3Key;
};
